import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const PythonNumbers = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Numeric Data Types</div>
                <h1>Numbers & Precision in Python</h1>
                <p className="hero-lead">
                    Master Python's dual-nature numeric system. From the "Infinite Tape" of arbitrary-precision integers
                    to the tricky world of IEEE 754 floating-point binaries, learn how to calculate with confidence.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    Numbers seem simple. We use them every day. But inside a computer, numbers are a minefield of
                    trade-offs between <strong>Speed</strong>, <strong>Memory</strong>, and <strong>Precision</strong>.
                </p>
                <p>
                    Most programming languages (like C or Java) strictly tie numbers to hardware limits (32-bit or 64-bit).
                    If you try to count too high, your number "overflows" and wraps around to a negative value.
                    Python is different. It prioritizes <strong>User Experience</strong> over raw hardware mapping.
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li><strong>The Infinite Tape:</strong> How Python integers grow forever (Arbitrary Precision).</li>
                        <li><strong>The Binary Trap:</strong> Why <code>0.1 + 0.2 != 0.3</code> (IEEE 754 Deep Dive).</li>
                        <li><strong>Bitwise Magic:</strong> Manipulating individual bits with <code>&</code>, <code>|</code>, and <code>^</code>.</li>
                        <li><strong>Complex Numbers:</strong> Using real and imaginary components for engineering.</li>
                        <li><strong>Financial Math:</strong> Using the <code>Decimal</code> module for exact currency calculations.</li>
                        <li><strong>Formatting Mastery:</strong> Controlling significant digits with f-strings.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>The Two Worlds of Python Numbers</h2>
                <p>
                    To write bug-free numerical code, you need two distinct mental models.
                </p>

                <div className="analogy-box">
                    <h3> Analogy: The Tape vs. The Slidewire</h3>
                    <div className="analogy-grid">
                        <div className="analogy-item">
                            <div className="analogy-icon"></div>
                            <strong>Integers (int) are an "Infinite Tape"</strong>
                            <p>
                                Imagine a measuring tape that magically grows longer whenever you need it.
                                You can write a number with 5 digits, or 5,000 digits. It will always be <strong>exact</strong>.
                                It never rounds, and it never runs out of space (until your RAM fills up).
                            </p>
                        </div>
                        <div className="analogy-item">
                            <div className="analogy-icon"></div>
                            <strong>Floats (float) are "Scientific Notation"</strong>
                            <p>
                                Imagine writing numbers on a small index card. You only have space for about 16 digits.
                                If the number is too long (like Pi), you mistakenly cut off the end.
                                This creates <strong>rounding errors</strong>.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>1. Integers: Arbitrary Precision</h2>
                <p>
                    In C, an integer is a fixed box (usually 4 bytes). In Python, an integer is a dynamic object.
                    This allows you to calculate numbers larger than the number of atoms in the universe.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Calculating the Impossible</span>
                    </div>
                    <CodeBlock language="python" code={`# A Googol (1 followed by 100 zeros)
googol = 10 ** 100
print(googol)
# Output: 100000... (lots of zeros) ...0000

# Factorials explode in size rapidly
import math
huge_num = math.factorial(100)
print(f"Digits in 100!: {len(str(huge_num))}")
# Output: Digits in 100!: 158

# C-style Overflow? Doesn't exist.
x = 2 ** 63 - 1  # Logic limit for 64-bit integers
print(x + 1)     # Python just expands the memory allocation.`} />
                </div>

                <h3>Performance Note</h3>
                <p>
                    This magic comes at a cost. A standard C integer takes <strong>4 bytes</strong>. A Python integer
                    object takes at least <strong>28 bytes</strong> plus size of the digits.
                    <br />
                    <em>Is it slow?</em> Yes, for high-performance number crunching (like video games or matrix multiplication),
                    pure Python integers are too slow. That's why we use libraries like <strong>NumPy</strong> (which brings C-style fixed-size integers back to Python).
                </p>
            </div>

            <div className="content-section">
                <h2>2. Bitwise Operations: The Hardware Level</h2>
                <p>
                    Integers aren't just numbers; they are sequences of bits. Python allows you to manipulate these bits directly,
                    which is crucial for cryptography, networking, and permissions systems.
                </p>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Operator</th>
                                <th>Name</th>
                                <th>Description</th>
                                <th>Example (0b101, 0b001)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>&</code></td>
                                <td>AND</td>
                                <td>Bits are 1 if BOTH are 1</td>
                                <td><code>5 & 1 {"-&gt;"} 1</code></td>
                            </tr>
                            <tr>
                                <td><code>|</code></td>
                                <td>OR</td>
                                <td>Bits are 1 if EITHER is 1</td>
                                <td><code>5 | 1 {"-&gt;"} 5</code></td>
                            </tr>
                            <tr>
                                <td><code>^</code></td>
                                <td>XOR</td>
                                <td>Bits are 1 if DIFFERENT</td>
                                <td><code>5 ^ 1 {"-&gt;"} 4</code></td>
                            </tr>
                            <tr>
                                <td><code>{'<<'}</code></td>
                                <td>Left Shift</td>
                                <td>Shift bits left (Multiply by 2)</td>
                                <td><code>5 {'<<'} 1 {"-&gt;"} 10</code></td>
                            </tr>
                            <tr>
                                <td><code>{'>>'}</code></td>
                                <td>Right Shift</td>
                                <td>Shift bits right (Divide by 2)</td>
                                <td><code>5 {'>>'} 1 {"-&gt;"} 2</code></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <CodeBlock language="python" code={`# Permissions Mockup
READ = 0b100  # 4
WRITE = 0b010 # 2
EXEC = 0b001  # 1

# My permissions: Read (4) + Execute (1) = 5
my_perms = READ | EXEC 

# Check if I have WRITE permissions
if my_perms & WRITE:
    print("Access Granted")
else:
    print("Access Denied") # Prints Denied`} />
            </div>

            <div className="content-section">
                <h2>3. Floats: The Binary Binary Trap</h2>
                <p>
                    Python <code>float</code> uses the IEEE 754 standard (Double Precision). It stores numbers using binary scientific notation:
                    <code>Sign * Mantissa * 2^Exponent</code>.
                </p>
                <p>
                    The problem is that <strong>humans count in Base-10</strong>, but <strong>computers count in Base-2</strong>.
                    Just as <code>1/3</code> cannot be written exactly in Base-10 (0.3333...),
                    <code>0.1</code> cannot be written exactly in Base-2.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>The "0.1" Illusion</span>
                    </div>
                    <CodeBlock language="python" code={`# The famous precision error
print(0.1 + 0.2)
# Output: 0.30000000000000004

# Why? Let's look at the true formatting for 18 digits
print(f"{0.1:.18f}")
# Output: 0.100000000000000006

# This means equality checks FAIL
if 0.1 + 0.2 == 0.3:
    print("Math works!")
else:
    print("Math is broken?")  # This prints!`} />
                </div>

                <h3>Solution: math.isclose()</h3>
                <p>
                    Never compare floats directly. Always ask: "Are these close enough?"
                </p>
                <CodeBlock language="python" code={`import math

a = 0.1 + 0.2
b = 0.3

# Check if they are close within a small tolerance (epsilon)
if math.isclose(a, b):
    print("Math works again!")`} />
            </div>

            <div className="content-section">
                <h2>4. The Decimal Module: Exact Math</h2>
                <p>
                    If you are building a banking app, "close enough" isn't good enough. You can't lose a penny due to rounding.
                    Enter the <code>Decimal</code> type. It stores numbers as decimal digits (Base-10), exactly as humans do.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Financial Precision</span>
                    </div>
                    <CodeBlock language="python" code={`from decimal import Decimal

#  WRONG: Creating Decimal from Float (inherits the error)
bad = Decimal(0.1)
print(bad)
# Output: 0.10000000000000000555...

# CORRECT: Creating Decimal from String
price = Decimal("19.99")
tax = Decimal("0.08")

total = price * (1 + tax)
print(total)
# Output: 21.5892 (Exact)`} />
                </div>
            </div>

            <div className="content-section">
                <h2>5. Complex Numbers: Engineering Power</h2>
                <p>
                    Python is one of the few languages with built-in support for Complex Numbers, widely used in
                    Electrical Engineering and Physics. They consist of a real part and an imaginary part (suffix <code>j</code>).
                </p>
                <CodeBlock language="python" code={`# Creating complex numbers
z = 3 + 4j
print(type(z))   # <class 'complex'>

# Attributes
print(z.real)    # 3.0
print(z.imag)    # 4.0

# Conjugate
print(z.conjugate()) # (3-4j)

# Arithmetic works natively
z2 = 1 - 1j
print(z + z2)    # (4+3j)`} />
            </div>

            <div className="content-section">
                <h2>Formatting: Making Numbers Pretty</h2>
                <p>
                    Often you have a float with 10 decimal places, but you only want to display 2.
                    Fast-Strings (f-strings) are the modern way to handle this.
                </p>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Format Code</th>
                                <th>Description</th>
                                <th>Example Input</th>
                                <th>Output</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>:.2f</code></td>
                                <td>Fixed point (2 decimals)</td>
                                <td><code>3.14159</code></td>
                                <td><code>"3.14"</code></td>
                            </tr>
                            <tr>
                                <td><code>:,</code></td>
                                <td>Comma separator</td>
                                <td><code>10000</code></td>
                                <td><code>"10,000"</code></td>
                            </tr>
                            <tr>
                                <td><code>:.0%</code></td>
                                <td>Percentage</td>
                                <td><code>0.25</code></td>
                                <td><code>"25%"</code></td>
                            </tr>
                            <tr>
                                <td><code>:03d</code></td>
                                <td>Zero padding (integers)</td>
                                <td><code>5</code></td>
                                <td><code>"005"</code></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <CodeBlock language="python" code={`pi = 3.14159265
revenue = 12500000.50

# Combining formats: Comma separator AND 2 decimal places
print(f"Revenue: $\\{revenue:,.2f}")
# Output: Revenue: $12,500,000.50

# Percentage
accuracy = 0.985
print(f"Accuracy: {accuracy:.1%}")
# Output: Accuracy: 98.5%`} />
            </div>

            <div className="content-section">
                <h2>Computer Science: Number Bases</h2>
                <p>
                    Sometimes you need to speak the machine's native language.
                    Python provides literals for Binary, Octal, and Hexadecimal.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Base Literals</span>
                    </div>
                    <CodeBlock language="python" code={`# 0b = Binary (Base-2)
x = 0b1010
print(f"Binary 1010 is {x} in Decimal")

# 0x = Hexadecimal (Base-16)
# Used heavily in colors (#FF00FF) and memory addresses
color = 0xFF
print(f"Hex FF is {color} in Decimal")

# Underscores for readability (Python 3.6+)
# Computers ignore them, but humans love them.
speed_of_light = 299_792_458
bytes_in_gb = 1_073_741_824`} />
                </div>
            </div>

            <div className="content-section best-practices">
                <h2>Best Practices & Takeaways</h2>
                <div className="practice-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use <code>math.isclose()</code> for float comparisons.</li>
                            <li>Use <code>from decimal import Decimal</code> for Money.</li>
                            <li>Use underscores (<code>1_000_000</code>) for long numbers.</li>
                            <li>Use f-strings (<code>:.2f</code>) for display formatting.</li>
                            <li>Use Bitwise operators only when explicitly manipulating flag bits.</li>
                        </ul>
                    </div>

                    <div className="practice-card">
                        <h3> Don't</h3>
                        <ul>
                            <li>Don't use <code>float</code> for banking apps.</li>
                            <li>Don't rely on <code>round()</code> for precise rounding logic (it has quirks).</li>
                            <li>Don't compare <code>x == 3.3</code> unless you know exactly what you are doing.</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default PythonNumbers;



